import { reactive, watch } from 'vue';
import UserConfigService from '@/core/services/UserConfigService';
import { getModuleIdByCode } from '@/core/services/Helper';
import eventBus from '@/core/plugins/event-bus';
import { widgetRegistry, defaultOrder, getTemplate } from './widgetRegistry';
import { fetchStatistics, fetchModuleWidgets } from './dashboardApi';
import { getRefreshMs } from './dashboardConfig';

const NAMESPACE = 'dashboard';

const MIN_COL = 1;
const MAX_COL = 4;
const MIN_ROW = 1;
const MAX_ROW = 3;

const state = reactive({
    instances: [],
    sizes: {},
    hidden: [],
    glassMode: true,
    customizeOpen: false,
    widgetData: {},
    widgetLoading: {},
    widgetError: {},
    widgetMissing: {},
    statisticsRaw: null,
});

const lastFetchedAt = {};
const dynamicParams = {};
let initialized = false;
let tickInterval = null;
let companyEpoch = 0;
const TICK_MS = 15000;

function clamp(v, min, max) {
    return Math.max(min, Math.min(max, v));
}

function _newId() {
    return 'inst_' + Math.random().toString(36).slice(2, 11) + Date.now().toString(36).slice(-4);
}

function _markFetched(ids) {
    const now = Date.now();
    ids.forEach(id => { lastFetchedAt[id] = now; });
}

function _migrateLegacy(saved) {
    if (Array.isArray(saved.instances)) return null;
    if (!Array.isArray(saved.order)) return null;
    const migrated = [];
    const seen = new Set();
    for (const tplId of saved.order) {
        if (seen.has(tplId)) continue;
        const tpl = getTemplate(tplId);
        if (!tpl) continue;
        seen.add(tplId);
        migrated.push({
            id: _newId(),
            templateId: tplId,
            config: { ...(tpl.defaultConfig || {}) },
        });
    }
    return migrated;
}

function init() {
    if (initialized) return;
    initialized = true;

    const saved = UserConfigService.get(NAMESPACE);

    let instances = null;
    if (Array.isArray(saved.instances)) {
        instances = saved.instances
            .filter(i => i && i.id && i.templateId && getTemplate(i.templateId))
            .map(i => ({ id: i.id, templateId: i.templateId, config: { ...(i.config || {}) } }));
    } else {
        instances = _migrateLegacy(saved);
    }

    if (!instances || !instances.length) {
        instances = defaultOrder
            .map(tplId => {
                const tpl = getTemplate(tplId);
                if (!tpl) return null;
                return { id: _newId(), templateId: tplId, config: { ...(tpl.defaultConfig || {}) } };
            })
            .filter(Boolean);
    } else {
        const existingTplIds = new Set(instances.map(i => i.templateId));
        defaultOrder.forEach(tplId => {
            if (existingTplIds.has(tplId)) return;
            const tpl = getTemplate(tplId);
            if (!tpl) return;
            if (tpl.multiInstance) return;
            instances.push({ id: _newId(), templateId: tplId, config: { ...(tpl.defaultConfig || {}) } });
        });
    }
    state.instances = instances;

    if (saved.sizes && typeof saved.sizes === 'object') {
        const sizes = {};
        const validIds = new Set(instances.map(i => i.id));
        for (const id of Object.keys(saved.sizes)) {
            if (!validIds.has(id)) continue;
            const s = saved.sizes[id] || {};
            sizes[id] = {
                col: clamp(parseInt(s.col, 10) || 1, MIN_COL, MAX_COL),
                row: clamp(parseInt(s.row, 10) || 1, MIN_ROW, MAX_ROW),
            };
        }
        state.sizes = sizes;
    }

    if (Array.isArray(saved.hidden)) {
        const validIds = new Set(instances.map(i => i.id));
        state.hidden = saved.hidden.filter(id => validIds.has(id));
    }

    if (typeof saved.glassMode === 'boolean') {
        state.glassMode = saved.glassMode;
    }

    watch(
        () => ({
            instances: state.instances,
            sizes: state.sizes,
            hidden: state.hidden,
            glassMode: state.glassMode,
        }),
        (val) => {
            UserConfigService.set(NAMESPACE, {
                instances: val.instances.map(i => ({ id: i.id, templateId: i.templateId, config: { ...i.config } })),
                sizes: { ...val.sizes },
                hidden: [...val.hidden],
                glassMode: val.glassMode,
            });
        },
        { deep: true },
    );

    eventBus.$on('setModules', () => { loadAll(); });
    eventBus.$on('setSelectCompany', () => {
        companyEpoch++;
        state.widgetData = {};
        state.widgetMissing = {};
        state.widgetError = {};
        state.widgetLoading = {};
        state.statisticsRaw = null;
        Object.keys(lastFetchedAt).forEach(k => delete lastFetchedAt[k]);
    });
}

function getTemplates() {
    return widgetRegistry;
}

function getInstance(id) {
    return state.instances.find(i => i.id === id) || null;
}

function getVisibleInstances() {
    const hiddenSet = new Set(state.hidden);
    return state.instances.filter(i => !hiddenSet.has(i.id));
}

function canAddTemplate(templateId) {
    const tpl = getTemplate(templateId);
    if (!tpl) return false;
    if (tpl.multiInstance) return true;
    return !state.instances.some(i => i.templateId === templateId);
}

function addInstance(templateId, config = {}) {
    const tpl = getTemplate(templateId);
    if (!tpl) return null;
    if (!canAddTemplate(templateId)) return null;
    const inst = {
        id: _newId(),
        templateId,
        config: { ...(tpl.defaultConfig || {}), ...config },
    };
    state.instances = [...state.instances, inst];
    return inst;
}

function removeInstance(id) {
    state.instances = state.instances.filter(i => i.id !== id);
    if (state.hidden.includes(id)) state.hidden = state.hidden.filter(x => x !== id);
    if (state.sizes[id]) {
        const s = { ...state.sizes };
        delete s[id];
        state.sizes = s;
    }
    delete state.widgetData[id];
    delete state.widgetLoading[id];
    delete state.widgetError[id];
    delete state.widgetMissing[id];
    delete dynamicParams[id];
    delete lastFetchedAt[id];
}

function updateInstanceConfig(id, config) {
    const idx = state.instances.findIndex(i => i.id === id);
    if (idx === -1) return;
    const updated = { ...state.instances[idx], config: { ...state.instances[idx].config, ...config } };
    state.instances = [...state.instances.slice(0, idx), updated, ...state.instances.slice(idx + 1)];
    delete lastFetchedAt[id];
    refreshInstance(id);
}

function setOrder(instanceIds) {
    const map = {};
    state.instances.forEach(i => { map[i.id] = i; });
    const ordered = [];
    const seen = new Set();
    for (const id of instanceIds) {
        if (seen.has(id) || !map[id]) continue;
        seen.add(id);
        ordered.push(map[id]);
    }
    state.instances.forEach(i => {
        if (!seen.has(i.id)) ordered.push(i);
    });
    state.instances = ordered;
}

function setSize(id, size) {
    if (!getInstance(id)) return;
    const col = clamp(parseInt(size.col, 10) || 1, MIN_COL, MAX_COL);
    const row = clamp(parseInt(size.row, 10) || 1, MIN_ROW, MAX_ROW);
    state.sizes = { ...state.sizes, [id]: { col, row } };
}

function toggleHidden(id) {
    if (!getInstance(id)) return;
    if (state.hidden.includes(id)) {
        state.hidden = state.hidden.filter(x => x !== id);
    } else {
        state.hidden = [...state.hidden, id];
    }
}

function isHidden(id) {
    return state.hidden.includes(id);
}

function toggleGlass() {
    state.glassMode = !state.glassMode;
}

function showAll() {
    state.hidden = [];
}

function hideAll() {
    state.hidden = state.instances.map(i => i.id);
}

function reset() {
    state.instances = defaultOrder
        .map(tplId => {
            const tpl = getTemplate(tplId);
            if (!tpl) return null;
            return { id: _newId(), templateId: tplId, config: { ...(tpl.defaultConfig || {}) } };
        })
        .filter(Boolean);
    state.sizes = {};
    state.hidden = [];
    state.glassMode = true;
    state.widgetData = {};
    state.widgetLoading = {};
    state.widgetError = {};
    state.widgetMissing = {};
    Object.keys(dynamicParams).forEach(k => delete dynamicParams[k]);
    Object.keys(lastFetchedAt).forEach(k => delete lastFetchedAt[k]);
}

function openCustomize() { state.customizeOpen = true; }
function closeCustomize() { state.customizeOpen = false; }
function toggleCustomize() { state.customizeOpen = !state.customizeOpen; }

function getWidgetData(id) { return state.widgetData[id] ?? null; }
function isWidgetLoading(id) { return !!state.widgetLoading[id]; }
function isWidgetMissing(id) { return !!state.widgetMissing[id]; }
function getWidgetError(id) { return state.widgetError[id] || null; }

function _setWidgetData(id, data) {
    state.widgetData = { ...state.widgetData, [id]: data };
    if (state.widgetMissing[id]) {
        const m = { ...state.widgetMissing }; delete m[id]; state.widgetMissing = m;
    }
    if (state.widgetError[id]) {
        const e = { ...state.widgetError }; delete e[id]; state.widgetError = e;
    }
}

function _setWidgetLoading(id, loading) {
    state.widgetLoading = { ...state.widgetLoading, [id]: loading };
}

function _setWidgetMissing(id, missing) {
    if (missing) {
        state.widgetMissing = { ...state.widgetMissing, [id]: true };
    } else {
        const m = { ...state.widgetMissing }; delete m[id]; state.widgetMissing = m;
    }
}

function _setWidgetError(id, err) {
    if (err) {
        state.widgetError = { ...state.widgetError, [id]: err };
    } else {
        const e = { ...state.widgetError }; delete e[id]; state.widgetError = e;
    }
}

function setWidgetParams(id, params) {
    dynamicParams[id] = { ...(params || {}) };
}

function clearWidgetParams(id) {
    delete dynamicParams[id];
}

function _resolveParams(template, instance) {
    const dyn = dynamicParams[instance.id];
    const def = template.source?.defaultParams;
    const fromConfig = template.buildParams ? template.buildParams(instance.config || {}) : null;
    if (!dyn && !def && !fromConfig) return null;
    return { ...(def || {}), ...(fromConfig || {}), ...(dyn || {}) };
}

async function _loadStatisticsGroup(visibleIds) {
    const instances = state.instances.filter(i => visibleIds.has(i.id));
    const statsInsts = instances.filter(i => {
        const tpl = getTemplate(i.templateId);
        return tpl?.source?.type === 'statistics';
    });
    if (!statsInsts.length) return;
    const epoch = companyEpoch;
    statsInsts.forEach(i => _setWidgetLoading(i.id, true));
    try {
        const stats = await fetchStatistics();
        if (epoch !== companyEpoch) return;
        state.statisticsRaw = stats;
        for (const inst of statsInsts) {
            const tpl = getTemplate(inst.templateId);
            const data = tpl?.extractStatistics ? tpl.extractStatistics(stats, inst.config || {}) : (stats?.[tpl?.source?.key] ?? null);
            if (data === null || data === undefined) {
                _setWidgetMissing(inst.id, true);
            } else {
                _setWidgetData(inst.id, data);
            }
        }
        _markFetched(statsInsts.map(i => i.id));
    } catch (err) {
        if (epoch !== companyEpoch) return;
        statsInsts.forEach(i => _setWidgetError(i.id, err.message || 'fetch_failed'));
    } finally {
        if (epoch === companyEpoch) {
            statsInsts.forEach(i => _setWidgetLoading(i.id, false));
        }
    }
}

async function _loadModuleGroup(visibleIds) {
    const specs = [];
    const callerMap = {};
    const targetIds = [];
    state.instances.forEach(inst => {
        if (!visibleIds.has(inst.id)) return;
        const tpl = getTemplate(inst.templateId);
        if (!tpl?.source || tpl.source.type !== 'module') return;
        const moduleCode = inst.config?.provider || tpl.source.moduleCode;
        const moduleId = getModuleIdByCode(moduleCode);
        if (!moduleId) {
            _setWidgetMissing(inst.id, true);
            return;
        }
        const spec = { module_id: moduleId, code: tpl.source.code, caller_id: inst.id };
        const params = _resolveParams(tpl, inst);
        if (params) spec.params = params;
        specs.push(spec);
        callerMap[inst.id] = true;
        targetIds.push(inst.id);
    });
    if (!specs.length) return;
    const epoch = companyEpoch;
    targetIds.forEach(id => _setWidgetLoading(id, true));
    try {
        const items = await fetchModuleWidgets(specs);
        if (epoch !== companyEpoch) return;
        for (const id of targetIds) {
            const found = items.find(w => w.caller_id === id);
            if (found && found.data) {
                _setWidgetData(id, found.data);
            } else {
                _setWidgetMissing(id, true);
            }
        }
        _markFetched(targetIds);
    } catch (err) {
        if (epoch !== companyEpoch) return;
        targetIds.forEach(id => _setWidgetError(id, err.message || 'fetch_failed'));
    } finally {
        if (epoch === companyEpoch) {
            targetIds.forEach(id => _setWidgetLoading(id, false));
        }
    }
}

async function loadAll() {
    const visible = getVisibleInstances();
    const visibleIds = new Set(visible.map(i => i.id));
    await Promise.all([
        _loadStatisticsGroup(visibleIds),
        _loadModuleGroup(visibleIds),
    ]);
}

async function refreshInstance(id) {
    const inst = getInstance(id);
    if (!inst) return;
    const tpl = getTemplate(inst.templateId);
    if (!tpl) return;
    const ids = new Set([id]);
    if (tpl.source?.type === 'statistics') {
        await _loadStatisticsGroup(ids);
    } else if (tpl.source?.type === 'module') {
        await _loadModuleGroup(ids);
    }
}

function _refreshTick() {
    const now = Date.now();
    const visible = getVisibleInstances();
    const dueIds = new Set();
    for (const inst of visible) {
        const tpl = getTemplate(inst.templateId);
        if (!tpl) continue;
        const ms = getRefreshMs(inst.templateId);
        if (ms <= 0) continue;
        const last = lastFetchedAt[inst.id] || 0;
        if (now - last >= ms) dueIds.add(inst.id);
    }
    if (!dueIds.size) return;

    const tasks = [];
    const hasStats = visible.some(i => dueIds.has(i.id) && getTemplate(i.templateId)?.source?.type === 'statistics');
    const hasModule = visible.some(i => dueIds.has(i.id) && getTemplate(i.templateId)?.source?.type === 'module');
    if (hasStats) tasks.push(_loadStatisticsGroup(dueIds));
    if (hasModule) tasks.push(_loadModuleGroup(dueIds));
    Promise.all(tasks);
}

function startAutoRefresh() {
    stopAutoRefresh();
    tickInterval = setInterval(_refreshTick, TICK_MS);
}

function stopAutoRefresh() {
    if (tickInterval) { clearInterval(tickInterval); tickInterval = null; }
}

export default {
    state,
    init,
    getTemplates,
    getInstance,
    getVisibleInstances,
    canAddTemplate,
    addInstance,
    removeInstance,
    updateInstanceConfig,
    setOrder,
    setSize,
    toggleHidden,
    isHidden,
    toggleGlass,
    showAll,
    hideAll,
    reset,
    openCustomize,
    closeCustomize,
    toggleCustomize,
    getWidgetData,
    isWidgetLoading,
    isWidgetMissing,
    getWidgetError,
    setWidgetParams,
    clearWidgetParams,
    loadAll,
    refreshInstance,
    startAutoRefresh,
    stopAutoRefresh,
    LIMITS: { MIN_COL, MAX_COL, MIN_ROW, MAX_ROW },
};
